import React, { useState } from 'react';
import axios from 'axios';

const ReturnStockHistory = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState({ returns: [], grand_totals: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '20px', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
    header: { color: '#2c3e50', textAlign: 'center', marginBottom: '30px' },
    form: { display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '30px', background: '#f8f9fa', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' },
    formGroup: { flex: 1, minWidth: '200px' },
    label: { display: 'block', marginBottom: '8px', fontWeight: '600', color: '#495057' },
    input: { width: '100%', padding: '10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '16px' },
    button: { backgroundColor: '#3498db', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '16px', alignSelf: 'flex-end', transition: 'background-color 0.3s' },
    buttonHover: { backgroundColor: '#2980b9' },
    buttonDisabled: { backgroundColor: '#95a5a6', cursor: 'not-allowed' },
    error: { color: '#e74c3c', marginTop: '10px', width: '100%' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' },
    tableHeaderCell: { padding: '12px 15px', textAlign: 'left', borderBottom: '1px solid #ddd', backgroundColor: '#3498db', color: 'white', fontWeight: '600' },
    tableCell: { padding: '12px 15px', textAlign: 'left', borderBottom: '1px solid #ddd' },
    tableCellRight: { padding: '12px 15px', textAlign: 'left', borderBottom: '1px solid #ddd' },
    evenRow: { backgroundColor: '#f8f9fa' },
    hoverRow: { backgroundColor: '#f1f1f1' },
    noData: { textAlign: 'center', padding: '20px', color: '#7f8c8d', fontStyle: 'italic' },
    totalsRow: { backgroundColor: '#e3f2fd', fontWeight: 'bold' },
    footerRow: { backgroundColor: '#bbdefb', fontWeight: 'bold' }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pak', {
      style: 'currency',
      currency: 'Pak',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/returnhistory', {
        params: { startdate: startDate, enddate: endDate }
      });
      setData(response.data);
    } catch (err) {
      setError('Failed to fetch return history. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Return Stock History</h2>
      
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <label htmlFor="startDate" style={styles.label}>Start Date:</label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={styles.input}
          />
        </div>
        
        <div style={styles.formGroup}>
          <label htmlFor="endDate" style={styles.label}>End Date:</label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={styles.input}
          />
        </div>
        
        <button 
          type="submit" 
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {}),
          }}
          onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = styles.buttonHover.backgroundColor)}
          onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = styles.button.backgroundColor)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Get History'}
        </button>
        
        {error && <div style={styles.error}>{error}</div>}
      </form>
      
      {data.returns.length > 0 ? (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeaderCell}>Product ID</th>
                <th style={styles.tableHeaderCell}>Product Name</th>
                <th style={styles.tableHeaderCell}>Pack Size</th>
                <th style={styles.tableHeaderCell}>Total Cotton</th>
                <th style={styles.tableHeaderCell}>Pcs</th>
                <th style={styles.tableHeaderCell}>Total Qty</th>
                <th style={styles.tableHeaderCell}>Sale Value</th>
                <th style={styles.tableHeaderCell}>Cost Value</th>
              </tr>
            </thead>
            <tbody>
              {data.returns.map((item, index) => (
                <tr 
                  key={index} 
                  style={index % 2 === 0 ? styles.evenRow : {}}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.hoverRow.backgroundColor}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? styles.evenRow.backgroundColor : 'transparent'}
                >
                  <td style={styles.tableCell}>{item.pro_id}</td>
                  <td style={styles.tableCell}>{item.product_name}</td>
                  <td style={styles.tableCellRight}>{formatNumber(item.pack_size)}</td>
                  <td style={styles.tableCellRight}>{formatNumber(item.total_cotton)}</td>
                  <td style={styles.tableCellRight}>{formatNumber(item.total_returned_units)}</td>
                  <td style={styles.tableCellRight}>{formatNumber(item.total_returned_qty)}</td>
                  <td style={styles.tableCellRight}>{formatCurrency(item.total_sale_value)}</td>
                  <td style={styles.tableCellRight}>{formatCurrency(item.total_cost_value)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={styles.footerRow}>
                <td style={styles.tableCell} colSpan="3">Grand Totals</td>
                <td style={styles.tableCellRight}>{formatNumber(data.grand_totals.total_cotton)}</td>
                <td style={styles.tableCellRight}>{formatNumber(data.grand_totals.total_units)}</td>
                <td style={styles.tableCellRight}>{formatNumber(data.grand_totals.total_qty)}</td>
                <td style={styles.tableCellRight}>{formatCurrency(data.grand_totals.total_sale_value)}</td>
                <td style={styles.tableCellRight}>{formatCurrency(data.grand_totals.total_cost_value)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        !loading && <div style={styles.noData}>No return history found for the selected period</div>
      )}
    </div>
  );
};

export default ReturnStockHistory;